import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Area, AreaChart, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import {
  Wallet, TrendingUp, CalendarClock, Target, Percent, Gauge, Activity,
  ArrowUpRight, Layers, LineChart, ShieldAlert,
} from "lucide-react";
import KpiCard from "@/components/KpiCard";
import MT5Status from "@/components/MT5Status";
import { Loading } from "@/components/Common";
import { PageHeader } from "@/components/Common";
import { EUR, EURc, pct, pnlColor, fmtClock } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Dashboard() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get("/dashboard").then((r) => r.data),
    refetchInterval: 20000,
  });

  if (isLoading || !data) return <Loading />;

  const spark = (data.sparkline || []).map((v, i) => ({ i, v }));
  const kpis = [
    { label: "Balance", value: EUR(data.account.balance), icon: Wallet, testId: "kpi-card-balance", sub: `Iniziale ${EUR(data.account.initial_balance)}`, big: true },
    { label: "Equity", value: EUR(data.equity), icon: LineChart, testId: "kpi-card-equity", sub: `Floating ${EURc(data.floating_pnl)}`, tone: data.floating_pnl >= 0 ? "profit" : "loss", big: true },
    { label: "P&L Oggi", value: EURc(data.pnl_today), icon: CalendarClock, testId: "kpi-card-today", tone: data.pnl_today >= 0 ? "profit" : "loss" },
    { label: "P&L Settimana", value: EURc(data.pnl_week), icon: TrendingUp, testId: "kpi-card-week", tone: data.pnl_week >= 0 ? "profit" : "loss" },
    { label: "P&L Mese", value: EURc(data.pnl_month), icon: TrendingUp, testId: "kpi-card-month", tone: data.pnl_month >= 0 ? "profit" : "loss" },
    { label: "P&L Totale", value: EURc(data.pnl_total), icon: Target, testId: "kpi-card-total", tone: data.pnl_total >= 0 ? "profit" : "loss", sub: pct(data.return_pct) + " return" },
    { label: "Win Rate", value: `${data.win_rate}%`, icon: Percent, testId: "kpi-card-winrate" },
    { label: "Profit Factor", value: data.profit_factor.toFixed(2), icon: Gauge, testId: "kpi-card-pf" },
    { label: "Expectancy", value: EUR(data.expectancy), icon: Target, testId: "kpi-card-expectancy" },
    { label: "Max Drawdown", value: EUR(data.max_drawdown), icon: ShieldAlert, testId: "kpi-card-dd", tone: "loss", sub: `${data.max_drawdown_pct}%` },
    { label: "Trade Totali", value: data.trade_count, icon: Layers, testId: "kpi-card-count" },
    { label: "Posizioni Aperte", value: data.open_positions, icon: Activity, testId: "kpi-card-open" },
  ];

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`Bentornato, ${user?.name?.split(" ")[0] || ""}`} subtitle="Panoramica del conto in tempo reale" />

      {/* Hero: balance + equity curve */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="dlx-card dlx-grain mb-6 overflow-hidden">
        <div className="grid gap-0 lg:grid-cols-[1.1fr_1.6fr]">
          <div className="border-b border-white/5 p-6 sm:p-8 lg:border-b-0 lg:border-r">
            <span className="font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Balance Attuale</span>
            <div className="mt-3 font-mono text-4xl font-bold text-white sm:text-5xl">{EUR(data.account.balance)}</div>
            <div className={`mt-2 flex items-center gap-2 font-mono text-sm ${pnlColor(data.pnl_total)}`}>
              <ArrowUpRight size={16} /> {EURc(data.pnl_total)} <span className="text-zinc-500">· {pct(data.return_pct)}</span>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div>
                <div className="font-sans text-[10px] uppercase tracking-widest text-zinc-500">Equity</div>
                <div className="mt-1 font-mono text-lg text-white">{EUR(data.equity)}</div>
              </div>
              <div>
                <div className="font-sans text-[10px] uppercase tracking-widest text-zinc-500">Floating</div>
                <div className={`mt-1 font-mono text-lg ${pnlColor(data.floating_pnl)}`}>{EURc(data.floating_pnl)}</div>
              </div>
            </div>
          </div>
          <div className="p-5 sm:p-6">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Equity · ultimi 30 giorni</span>
              <Link to="/performance" className="font-sans text-[11px] text-gold hover:underline" data-testid="dashboard-view-performance">
                Performance →
              </Link>
            </div>
            <ResponsiveContainer width="100%" height={210}>
              <AreaChart data={spark} margin={{ top: 10, right: 6, left: 6, bottom: 0 }}>
                <defs>
                  <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <YAxis domain={["dataMin", "dataMax"]} hide />
                <Tooltip contentStyle={{ background: "#0F0F12", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ display: "none" }} formatter={(v) => [EUR(v), "Balance"]} />
                <Area type="monotone" dataKey="v" stroke="#D4AF37" strokeWidth={2} fill="url(#goldFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4" data-testid="kpi-grid">
        {kpis.map((k, i) => (
          <KpiCard key={k.label} {...k} delay={i * 0.03} />
        ))}
      </div>
    </div>
  );
}
